import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
} from 'react';

import { logButtonClick } from 'features/navigation/utils/analytics';
import { StoreLocatorEmptyStates } from 'features/store/components/store-locator-empty-states';
import { IStore, mapStoreResponse } from 'features/store/pages/pickup-store-selector/ducks/stores';
import isEqual from 'lodash-es/isEqual';
import { FlatList, View } from 'react-native';

import { IRestaurant } from '@rbi-ctg/store';
import { ClickEventComponentNames, CustomEventNames } from 'state/amplitude';
import { useGeolocation } from 'state/geolocation';
import { t } from 'state/intl/translations';

import { AvailableAtNearbyRestaurantsHeader } from '../available-at-nearby-restaurants-header';
import { LoadingIndicator } from '../loading-indicator';
import { StoreCard } from '../store-card';
import { FocusedBy, StoreCardListRef, StoreLocatorActionType } from '../types';

import { IStoreCardListProps, StoreCardItemProps } from './types';

const ESTIMATED_COLLAPSED_CARD_HEIGHT = 160;

const areItemPropsEqual = (prev: StoreCardItemProps, next: StoreCardItemProps) => {
  const prevIsSelected = prev.item._id === prev.focusedId;
  const nextIsSelected = next.item._id === next.focusedId;

  if (prevIsSelected !== nextIsSelected) {
    return false;
  }

  return (
    prev.item === next.item &&
    prev.isLastItem === next.isLastItem &&
    prev.store === next.store &&
    prev.tab === next.tab &&
    prev.isInSupportForm === next.isInSupportForm &&
    prev.isOffersSelection === next.isOffersSelection &&
    prev.isOfferEligible === next.isOfferEligible &&
    prev.ineligibleStoreMessage === next.ineligibleStoreMessage
  );
};

const StoreCardItem = React.memo((props: StoreCardItemProps) => {
  const {
    item,
    focusedId,
    isLastItem,
    store,
    tab,
    onFavoriteClick,
    handleOnCardPress,
    isInSupportForm,
    isOffersSelection,
    onSelectStore,
    onLayoutHeight,
    isOfferEligible = true,
    ineligibleStoreMessage,
  } = props;

  const handleLayout = useCallback(
    (e: any) => {
      if (store?._id) {
        onLayoutHeight(store._id, e.nativeEvent.layout.height);
      }
    },
    [store?._id, onLayoutHeight]
  );

  if (!store?._id) {
    return null;
  }

  return (
    <View onLayout={handleLayout}>
      <StoreCard
        tab={tab}
        restaurant={store}
        onFavoriteClick={onFavoriteClick}
        onCardPress={handleOnCardPress}
        isSelected={item._id === focusedId}
        isInSupportForm={isInSupportForm}
        isOffersSelection={isOffersSelection}
        isOfferEligible={isOfferEligible}
        isLastItem={isLastItem}
        onSelectStore={onSelectStore}
        ineligibleStoreMessage={ineligibleStoreMessage}
      />
    </View>
  );
}, areItemPropsEqual);

const StoreCardList = forwardRef<StoreCardListRef, IStoreCardListProps>(
  (
    {
      currentTabHasError = false,
      isSearching,
      restaurants = [],
      tab,
      onSelectStore,
      isInSupportForm,
      isOffersSelection,
      restaurantFocused,
      onRestaurantHighlight,
      storesById,
      onFavoriteClick,
      actions,
      isStoreEligible,
      activeStore,
      ineligibleStoreMessage,
    },
    ref
  ) => {
    const { coordinatesAvailable, isPermissionGranted } = useGeolocation();
    const flatListRef = useRef<FlatList | null>(null);
    const itemHeights = useRef<{ [key: string]: number }>({});
    const scrollTimerRef = useRef<NodeJS.Timeout | null>(null);

    useEffect(() => {
      return () => {
        if (scrollTimerRef.current) {
          clearTimeout(scrollTimerRef.current);
        }
      };
    }, []);

    const { listRestaurants, showNearbySectionHeader } = useMemo(() => {
      if (!isOffersSelection) {
        return { listRestaurants: restaurants, showNearbySectionHeader: false };
      }

      const eligibleNearby = restaurants.filter(store => {
        if (activeStore?._id && store._id === activeStore._id) {
          return false;
        }
        return isStoreEligible ? isStoreEligible(store) : true;
      });

      if (activeStore?._id) {
        return {
          listRestaurants: [activeStore, ...eligibleNearby],
          showNearbySectionHeader: eligibleNearby.length > 0,
        };
      }

      return { listRestaurants: eligibleNearby, showNearbySectionHeader: false };
    }, [activeStore, isOffersSelection, isStoreEligible, restaurants]);

    const getStoreById = useCallback(
      (storeId: string) => {
        return storesById?.[storeId];
      },
      [storesById]
    );

    const handleOnCardPress = useCallback(
      ({ restaurant }: { restaurant: IRestaurant }) => {
        actions.current[StoreLocatorActionType.USER_SELECTION] = true;
        const restaurantToIStore = mapStoreResponse(restaurant);
        onRestaurantHighlight({ restaurant: restaurantToIStore, focusedBy: FocusedBy.LIST });
        if (restaurant._id) {
          logButtonClick({
            attributes: {
              StoreId: restaurant._id,
              Name: CustomEventNames.BUTTON_CLICK_STORE_SELECTED,
              name: CustomEventNames.BUTTON_CLICK_STORE_SELECTED,
              component: ClickEventComponentNames.BUTTON,
            },
          });
        }
      },
      [actions, onRestaurantHighlight]
    );

    const scrollToTop = useCallback(() => {
      if (!flatListRef.current || !listRestaurants.length) {
        return;
      }

      if (scrollTimerRef.current) {
        clearTimeout(scrollTimerRef.current);
      }
      scrollTimerRef.current = setTimeout(() => {
        flatListRef.current?.scrollToOffset({ animated: true, offset: 0 });
      }, 0);
    }, [listRestaurants]);

    const scrollToRestaurant = useCallback(
      (restaurantId: string) => {
        if (!flatListRef.current || !listRestaurants.length) {
          return;
        }

        const restaurantIndex = listRestaurants.findIndex(
          restaurant => restaurant._id === restaurantId
        );
        if (restaurantIndex !== -1) {
          if (scrollTimerRef.current) {
            clearTimeout(scrollTimerRef.current);
          }
          scrollTimerRef.current = setTimeout(() => {
            let offset = 0;
            for (let i = 0; i < restaurantIndex; i++) {
              const id = listRestaurants[i]?._id;
              offset +=
                (id ? itemHeights.current[id] : ESTIMATED_COLLAPSED_CARD_HEIGHT) ||
                ESTIMATED_COLLAPSED_CARD_HEIGHT;
            }

            flatListRef.current?.scrollToOffset({
              animated: true,
              offset,
            });
          }, 0);
        }
      },
      [listRestaurants]
    );

    useImperativeHandle(
      ref,
      () => ({
        scrollToTop,
        scrollToRestaurant,
      }),
      [scrollToRestaurant, scrollToTop]
    );

    const handleLayoutHeight = useCallback((storeId: string, height: number) => {
      itemHeights.current[storeId] = height;
    }, []);

    const renderItem = useCallback(
      ({ item, index }: { item: IStore; index: number }) => {
        const isLastItem = index === listRestaurants.length - 1;
        const store = item._id
          ? getStoreById(item._id) ?? ((item as unknown) as IRestaurant)
          : null;
        const isPinnedCurrent = Boolean(
          isOffersSelection && activeStore?._id && item._id === activeStore._id
        );
        const showHeaderBeforeItem =
          showNearbySectionHeader &&
          ((isPinnedCurrent === false && index === 1 && Boolean(activeStore?._id)) ||
            (!activeStore?._id && index === 0));

        return (
          <>
            {showHeaderBeforeItem ? <AvailableAtNearbyRestaurantsHeader /> : null}
            <StoreCardItem
              item={item}
              focusedId={restaurantFocused?._id}
              isLastItem={isLastItem}
              store={store}
              tab={tab}
              onFavoriteClick={onFavoriteClick}
              handleOnCardPress={handleOnCardPress}
              isInSupportForm={isInSupportForm}
              isOffersSelection={isOffersSelection}
              onSelectStore={onSelectStore}
              onLayoutHeight={handleLayoutHeight}
              isOfferEligible={isStoreEligible ? isStoreEligible(item) : true}
              ineligibleStoreMessage={ineligibleStoreMessage}
            />
          </>
        );
      },
      [
        listRestaurants.length,
        getStoreById,
        restaurantFocused?._id,
        onSelectStore,
        isInSupportForm,
        isOffersSelection,
        handleOnCardPress,
        onFavoriteClick,
        tab,
        handleLayoutHeight,
        isStoreEligible,
        showNearbySectionHeader,
        activeStore?._id,
        ineligibleStoreMessage,
      ]
    );

    if (isSearching) {
      return <LoadingIndicator ariaLabel={t('locationSearch')} />;
    }

    if (listRestaurants.length === 0) {
      return (
        <StoreLocatorEmptyStates
          tab={tab}
          locationPermissionsGranted={isPermissionGranted}
          coordinatesAvailable={coordinatesAvailable}
          currentTabError={currentTabHasError}
          wrapperStyle={{
            width: '100%',
          }}
        />
      );
    }

    return (
      <FlatList
        keyExtractor={(item, index) => item?._id || index + '_restaurant'}
        data={listRestaurants}
        extraData={restaurantFocused?._id}
        renderItem={renderItem}
        ref={flatListRef}
      />
    );
  }
);

export default React.memo(StoreCardList, (prevProps, nextProps) => {
  if (prevProps.restaurantFocused !== nextProps.restaurantFocused) {
    return false;
  }
  if (prevProps.tab !== nextProps.tab) {
    return false;
  }
  if (prevProps.isSearching !== nextProps.isSearching) {
    return false;
  }
  if (prevProps.currentTabHasError !== nextProps.currentTabHasError) {
    return false;
  }
  if (prevProps.activeStore?._id !== nextProps.activeStore?._id) {
    return false;
  }
  if (prevProps.isStoreEligible !== nextProps.isStoreEligible) {
    return false;
  }
  if (
    !isEqual(
      prevProps?.restaurants?.map(x => x._id),
      nextProps?.restaurants?.map(x => x._id)
    )
  ) {
    return false;
  }

  if (prevProps.ref !== nextProps.ref) {
    return false;
  }

  return true;
});
